<template>
    <div class="thr-wrap">

        <!-- Page Header -->
        <div class="thr-header mb-4">
            <div class="thr-header-left">
                <div class="thr-header-icon">
                    <b-icon icon="lightning-fill" />
                </div>
                <div>
                    <h4 class="thr-title mb-0">{{ $route.params.name }}</h4>
                    <span class="thr-subtitle">{{ $t('collectionData') }}</span>
                </div>
            </div>
            <div class="thr-header-right">
                <div class="thr-env-tabs">
                    <button
                        class="thr-env-tab"
                        :class="{ 'thr-env-tab--active': env === 'dev' }"
                        @click="changeEnv('dev')">{{ $t('dev') }}</button>
                    <button
                        class="thr-env-tab"
                        :class="{ 'thr-env-tab--active': env === 'live' }"
                        @click="changeEnv('live')">{{ $t('live') }}</button>
                </div>
                <button class="thr-btn thr-btn--primary" @click="addData">
                    <b-icon icon="plus" class="mr-1" />{{ $t('addData') }}
                </button>
                <button class="thr-btn thr-btn--danger-outline" @click="dropResource">
                    {{ $t('drop') }}
                </button>
            </div>
        </div>

        <!-- Filter Bar -->
        <div class="thr-filter-bar mb-3">
            <div class="thr-filter-input-wrap">
                <b-icon icon="search" class="thr-filter-icon" />
                <b-form-input
                    v-model="filterField"
                    class="thr-filter-input"
                    :placeholder="$t('filters')"
                    @keyup.enter.native="fetchData" />
            </div>
            <button class="thr-btn thr-btn--secondary" @click="fetchData">
                {{ $t('find') }}
            </button>
        </div>

        <!-- Skeleton Loading -->
        <div v-if="!resource">
            <div v-for="i in 5" :key="i" class="thr-skeleton mb-2"></div>
        </div>

        <!-- Empty State -->
        <div v-else-if="resource.items && resource.items.length === 0" class="thr-empty">
            <div class="thr-empty-icon-wrap">
                <b-icon icon="database" />
            </div>
            <p class="thr-empty-text">{{ $t('noResources') }}</p>
            <button class="thr-btn thr-btn--primary" @click="addData">
                <b-icon icon="plus" class="mr-1" />{{ $t('addData') }}
            </button>
        </div>

        <!-- Records List -->
        <div v-else>
            <div
                v-for="data in resource.items"
                :key="data.id"
                class="thr-record mb-2"
                :class="{ 'thr-record--active': isExpanded(data.id) }">

                <!-- Row: collapsed summary -->
                <div class="thr-record-header" @click="toggleExpand(data.id, data)">
                    <div class="thr-record-meta">
                        <span class="thr-record-inc">#{{ data.incrementId }}</span>
                        <code class="thr-record-oid">{{ data.id }}</code>
                    </div>
                    <!-- top-level field preview chips -->
                    <div class="thr-record-chips">
                        <span
                            v-for="(val, key) in getPreviewFields(data)"
                            :key="key"
                            class="thr-chip">
                            <span class="thr-chip-key">{{ key }}</span>
                            <span class="thr-chip-val">{{ formatChipVal(val) }}</span>
                        </span>
                    </div>
                    <b-icon
                        :icon="isExpanded(data.id) ? 'chevron-up' : 'chevron-down'"
                        class="thr-record-chevron" />
                </div>

                <!-- Expanded body -->
                <div class="thr-record-body" v-if="isExpanded(data.id)">

                    <!-- View / Edit toggle -->
                    <div class="thr-mode-tabs mb-3">
                        <button
                            class="thr-mode-tab"
                            :class="{ 'thr-mode-tab--active': !isEditing(data.id) }"
                            @click.stop="setViewMode(data.id)">
                            <b-icon icon="eye" class="mr-1" />Inspect
                        </button>
                        <button
                            class="thr-mode-tab"
                            :class="{ 'thr-mode-tab--active': isEditing(data.id) }"
                            @click.stop="setEditMode(data.id, data)">
                            <b-icon icon="pencil" class="mr-1" />Edit
                        </button>
                    </div>

                    <!-- JSON Tree Viewer (inspect mode) -->
                    <div class="thr-json-viewer" v-if="!isEditing(data.id)">
                        <json-tree-view
                            :data="getCleanData(data)"
                            :maxDepth="4"
                            :readOnly="true" />
                    </div>

                    <!-- Code Editor (edit mode) -->
                    <div v-else>
                        <code-editor
                            :value="getValue(data)"
                            :withButton="false"
                            @input="(e) => updateData(data.id, e)" />
                    </div>

                    <!-- Footer actions -->
                    <div class="thr-record-footer">
                        <template v-if="isEditing(data.id)">
                            <button class="thr-btn thr-btn--success" @click.stop="saveData(data.id)">
                                {{ $t('edit') }}
                            </button>
                            <button class="thr-btn thr-btn--secondary" @click.stop="setViewMode(data.id)">
                                Cancel
                            </button>
                        </template>
                        <button class="thr-btn thr-btn--danger-outline" @click.stop="removeData(data.id)">
                            {{ $t('delete') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div class="thr-pagination mt-3">
                <span class="thr-page-info" v-if="resource">
                    {{ resource.total }} {{ $t('total') }}
                </span>
                <div class="thr-page-nav">
                    <button class="thr-page-btn" @click="prevPage" :disabled="page < 2">
                        <b-icon icon="arrow-left-short" />
                    </button>
                    <span class="thr-page-num">{{ page }} / {{ pages }}</span>
                    <button class="thr-page-btn" @click="nextPage" :disabled="page >= pages">
                        <b-icon icon="arrow-right-short" />
                    </button>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import CodeEditor from "@/components/form/Editor/Code"
import JsonTreeView from "@/components/elements/JsonTreeView"

const INTERNAL_KEYS = ['id', '__v', 'incrementId']
const PREVIEW_LIMIT = 4

export default {
    name: 'Resource',
    components: {
        CodeEditor,
        JsonTreeView
    },
    data: function() {
        return {
            resource: null,
            expandedIds: [],
            editingIds: [],
            resourceData: {},
            env: this.$store.getters.getCurrentEnv,
            filterField: null,
            limit: 12,
            page: 1
        }
    },
    computed: {
        filters() {
            if (!this.filterField) return null
            try { return JSON.parse(this.filterField) } catch(e) { return null }
        },
        pages() {
            if (this.resource) return this.resource.pages
            return 1
        }
    },
    methods: {
        /* ── Expand / collapse ─────────────────────── */
        isExpanded(id) {
            return this.expandedIds.indexOf(id) !== -1
        },
        toggleExpand(id, data) {
            if (this.isExpanded(id)) {
                this.expandedIds = this.expandedIds.filter(function(i) { return i !== id })
            } else {
                this.expandedIds.push(id)
                // seed edit buffer so code editor has content if user switches
                if (this.resourceData[id] === undefined) {
                    this.resourceData[id] = JSON.stringify(this.getCleanData(data), null, 2)
                }
            }
        },

        /* ── View / edit mode ──────────────────────── */
        isEditing(id) {
            return this.editingIds.indexOf(id) !== -1
        },
        setEditMode(id, data) {
            if (!this.isEditing(id)) this.editingIds.push(id)
            if (this.resourceData[id] === undefined) {
                this.resourceData[id] = JSON.stringify(this.getCleanData(data), null, 2)
            }
        },
        setViewMode(id) {
            this.editingIds = this.editingIds.filter(function(i) { return i !== id })
        },

        /* ── Data helpers ──────────────────────────── */
        getCleanData(d) {
            var out = {}
            var keys = Object.keys(d)
            for (var i = 0; i < keys.length; i++) {
                if (keys[i] !== '__v') out[keys[i]] = d[keys[i]]
            }
            return out
        },

        getValue(d) {
            return this.resourceData[d.id] !== undefined
                ? this.resourceData[d.id]
                : JSON.stringify(this.getCleanData(d), null, 2)
        },

        getPreviewFields(d) {
            var out = {}
            var count = 0
            var keys = Object.keys(d)
            for (var i = 0; i < keys.length; i++) {
                var k = keys[i]
                if (INTERNAL_KEYS.indexOf(k) !== -1) continue
                if (count >= PREVIEW_LIMIT) break
                out[k] = d[k]
                count++
            }
            return out
        },

        formatChipVal(v) {
            if (v === null) return 'null'
            if (typeof v === 'object') return Array.isArray(v) ? '[…]' : '{…}'
            var s = String(v)
            return s.length > 22 ? s.slice(0, 22) + '…' : s
        },

        updateData(id, data) {
            this.resourceData[id] = data
        },

        /* ── CRUD ──────────────────────────────────── */
        saveData(id) {
            if (this.resourceData[id] === undefined) return

            let popup = this.$loadingPopup()

            this.$http.put("/v3/system/thunder", {
                type: this.$route.params.name,
                filters: { id: id },
                data: JSON.parse(this.resourceData[id]),
                flag: "Upsert"
            }, {
                headers: { 'X-Project-Env': this.env }
            }).then(() => {
                this.setViewMode(id)
                this.fetchData()
            }).catch(err => {
                this.$debugLog(err)
                this.$mainDialog.msgBoxOk(err.response.data.message)
            }).finally(() => {
                popup.hide()
            })
        },

        removeData(id) {
            this.$mainDialog.msgBoxConfirm(`${this.$t('areYouSure')}?`).then(resp => {
                if (resp) {
                    this.$http.put("/v3/system/thunder", {
                        type: this.$route.params.name,
                        filters: { id: id },
                        flag: "Delete"
                    }, {
                        headers: { 'X-Project-Env': this.env }
                    }).then(() => {
                        this.resource.items = this.resource.items.filter(function(d) { return d.id != id })
                        this.expandedIds = this.expandedIds.filter(function(i) { return i !== id })
                        this.editingIds = this.editingIds.filter(function(i) { return i !== id })
                    }).catch(err => {
                        this.$debugLog(err)
                        if (err.response) {
                            this.$bvToast.toast(err.response.data.message, { variant: 'danger', title: this.$t('error') })
                            return
                        }
                        this.$bvToast.toast(this.$t('unexpectedError'), { variant: 'danger', title: this.$t('error') })
                    })
                }
            })
        },

        addData() {
            let popup = this.$loadingPopup(`${this.$t('addingResource')}...`)

            this.$thunder(this.env).write({
                type: this.$route.params.name,
                filters: { incrementId: this.resource.total },
                data: { incrementId: this.resource.total },
                flag: "InsertOne"
            }).then(() => {
                this.fetchData()
            }).catch(err => {
                this.$debugLog(err)
                if (err.response) {
                    this.$bvToast.toast(err.response.data.message, { variant: 'danger', title: this.$t('error') })
                    return
                }
                this.$bvToast.toast(this.$t('unexpectedError'), { variant: 'danger', title: this.$t('error') })
            }).finally(() => {
                popup.hide()
            })
        },

        async dropResource() {
            const confirm = await this.$deleteConfirmationPrompt()
            if (!confirm) return

            let popup = this.$loadingPopup(`${this.$t('removingResource')}...`)

            this.$http.delete(`/v3/system/thunder/${this.$route.params.name}`)
            .then(() => {
                this.$router.push("/admin/thunder")
            }).catch(err => {
                this.$debugLog(err)
                this.$mainDialog.msgBoxOk(`${this.$t('failedToDrop')}.`)
            }).finally(() => {
                popup.hide()
            })
        },

        fetchData() {
            this.resource = null
            this.expandedIds = []
            this.editingIds = []
            this.resourceData = {}

            this.$thunder(this.env).read({
                type: this.$route.params.name,
                filters: this.filters,
                flag: "Paginate",
                options: {
                    skip: (this.page - 1) * this.limit,
                    limit: this.limit,
                    sort: [{ key: "incrementId", value: -1 }]
                }
            }).then(data => {
                this.resource = data || []
            }).catch(err => {
                this.$debugLog(err)
                this.$bvToast.toast(this.$t('failedToFetchResource'), { variant: 'danger', title: this.$t('error') })
            })
        },

        changeEnv(env) {
            this.env = env
            this.fetchData()
        },

        prevPage() {
            this.page--
            if (this.page < 1) this.page = 1
            this.fetchData()
        },

        nextPage() {
            this.page++
            this.fetchData()
        }
    },
    mounted() {
        this.fetchData()
    }
}
</script>

<style scoped>
.thr-wrap {
    padding: 0 4px;
}

/* ── Header ────────────────────────────────── */
.thr-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}
.thr-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
}
.thr-header-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: var(--primary);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
}
.thr-title {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--text-primary);
    text-transform: capitalize;
}
.thr-subtitle {
    font-size: 0.77rem;
    color: var(--text-muted, #8a8fa8);
}
.thr-header-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

/* ── Env switcher ───────────────────────────── */
.thr-env-tabs {
    display: flex;
    border: 1px solid var(--border-default);
    border-radius: 8px;
    overflow: hidden;
    background: var(--bg-card);
}
.thr-env-tab {
    padding: 6px 16px;
    font-size: 0.8rem;
    font-weight: 500;
    background: transparent;
    border: none;
    color: var(--text-primary);
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    text-transform: capitalize;
}
.thr-env-tab--active {
    background: var(--primary);
    color: #fff;
}

/* ── Buttons ────────────────────────────────── */
.thr-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 7px 16px;
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 500;
    border: 1px solid transparent;
    cursor: pointer;
    transition: opacity 0.15s;
    white-space: nowrap;
    line-height: 1.4;
}
.thr-btn:hover { opacity: 0.82; }
.thr-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.thr-btn--primary    { background: var(--primary); color: #fff; border-color: var(--primary); }
.thr-btn--secondary  { background: var(--bg-card); color: var(--text-primary); border-color: var(--border-default); }
.thr-btn--success    { background: #2dce89; color: #fff; border-color: #2dce89; }
.thr-btn--danger-outline { background: transparent; color: #f5365c; border-color: #f5365c; }

/* ── Filter bar ─────────────────────────────── */
.thr-filter-bar {
    display: flex;
    gap: 10px;
    align-items: center;
}
.thr-filter-input-wrap {
    position: relative;
    flex: 1;
}
.thr-filter-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted, #8a8fa8);
    font-size: 13px;
    z-index: 2;
    pointer-events: none;
}
.thr-filter-input {
    padding-left: 36px !important;
    border-radius: 8px !important;
    border-color: var(--border-default) !important;
    background: var(--bg-card) !important;
    color: var(--text-primary) !important;
    font-family: 'Fira Code', 'JetBrains Mono', monospace;
    font-size: 0.82rem !important;
}

/* ── Skeleton ───────────────────────────────── */
.thr-skeleton {
    height: 54px;
    border-radius: 10px;
    background: linear-gradient(90deg, var(--bg-card) 25%, var(--border-default) 50%, var(--bg-card) 75%);
    background-size: 200% 100%;
    animation: thr-shimmer 1.4s infinite;
}
@keyframes thr-shimmer {
    0%   { background-position: 200% center; }
    100% { background-position: -200% center; }
}

/* ── Empty state ────────────────────────────── */
.thr-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 72px 20px;
    text-align: center;
    gap: 16px;
}
.thr-empty-icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    color: var(--text-muted, #8a8fa8);
}
.thr-empty-text {
    color: var(--text-muted, #8a8fa8);
    font-size: 0.88rem;
    margin: 0;
}

/* ── Records ────────────────────────────────── */
.thr-record {
    border: 1px solid var(--border-default);
    border-radius: 10px;
    background: var(--bg-card);
    overflow: hidden;
    transition: border-color 0.15s;
}
.thr-record--active {
    border-color: var(--primary);
}

.thr-record-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 18px;
    cursor: pointer;
    user-select: none;
    transition: background 0.12s;
}
.thr-record-header:hover {
    background: rgba(0,0,0,0.025);
}

.thr-record-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}
.thr-record-inc {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--primary);
    background: rgba(79,70,229,0.08);
    padding: 2px 8px;
    border-radius: 5px;
    flex-shrink: 0;
}
.thr-record-oid {
    font-size: 0.7rem;
    color: var(--text-muted, #8a8fa8);
    font-family: 'Fira Code', 'JetBrains Mono', monospace;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
}

/* ── Preview chips ──────────────────────────── */
.thr-record-chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    flex: 1;
    min-width: 0;
    overflow: hidden;
}
.thr-chip {
    display: inline-flex;
    align-items: center;
    gap: 0;
    border: 1px solid var(--border-default);
    border-radius: 5px;
    font-size: 0.72rem;
    overflow: hidden;
    max-width: 180px;
}
.thr-chip-key {
    padding: 2px 6px;
    background: var(--border-default);
    color: var(--text-muted, #8a8fa8);
    font-weight: 500;
    white-space: nowrap;
}
.thr-chip-val {
    padding: 2px 7px;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.thr-record-chevron {
    color: var(--text-muted, #8a8fa8);
    font-size: 14px;
    flex-shrink: 0;
    margin-left: auto;
}

/* ── Record body ────────────────────────────── */
.thr-record-body {
    border-top: 1px solid var(--border-default);
    padding: 16px 18px;
}

/* ── Mode tabs ──────────────────────────────── */
.thr-mode-tabs {
    display: inline-flex;
    border: 1px solid var(--border-default);
    border-radius: 7px;
    overflow: hidden;
    background: var(--bg-card);
}
.thr-mode-tab {
    padding: 5px 14px;
    font-size: 0.78rem;
    font-weight: 500;
    background: transparent;
    border: none;
    color: var(--text-muted, #8a8fa8);
    cursor: pointer;
    transition: background 0.14s, color 0.14s;
    display: inline-flex;
    align-items: center;
}
.thr-mode-tab--active {
    background: var(--primary);
    color: #fff;
}

/* ── JSON viewer wrapper ────────────────────── */
.thr-json-viewer {
    background: var(--form-bg, var(--bg-card));
    border: 1px solid var(--border-default);
    border-radius: 8px;
    padding: 14px 16px;
    max-height: 480px;
    overflow-y: auto;
    font-size: 13px;
}

/* json-tree-view uses CSS vars already; just ensure dark/light background fits */
.thr-json-viewer >>> .json-tree-view {
    background: transparent;
}

.thr-record-footer {
    display: flex;
    gap: 10px;
    margin-top: 14px;
}

/* ── Pagination ─────────────────────────────── */
.thr-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 2px;
}
.thr-page-info {
    font-size: 0.8rem;
    color: var(--text-muted, #8a8fa8);
}
.thr-page-nav {
    display: flex;
    align-items: center;
    gap: 6px;
}
.thr-page-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid var(--border-default);
    background: var(--bg-card);
    color: var(--text-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.13s, color 0.13s, border-color 0.13s;
    font-size: 16px;
    padding: 0;
}
.thr-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.thr-page-btn:not(:disabled):hover {
    background: var(--primary);
    color: #fff;
    border-color: var(--primary);
}
.thr-page-num {
    font-size: 0.82rem;
    font-weight: 500;
    color: var(--text-primary);
    min-width: 48px;
    text-align: center;
}
</style>
